import React, { FC, useState, useCallback } from 'react';
import { isEmpty, isEqual, trim } from 'lodash';
import {  INPUT_MASK, MIN_LENGTH, SUPPORTED_CC } from '@trip-ux/constants';
import { Error } from '../Error';
import CreditCardIFrame from './CreditCardIframe';
import { getCardType } from './iframe.utils';
import { Field } from 'react-final-form';
import { GenericTextInput } from '@trip-ux/react-containers';
import { TravellerPaymentStore } from '@trip-ux/redux-store';

export type CreditCardVariant = 'COVER_ME' | 'DEFAULT';

export interface CreditCardFormProps {
  uri: string;
  id: string;
  onDSToken: (e: { token: string; type: string }, paxId) => void;
  travellerPayments: TravellerPaymentStore;
  onError: (errors: string[], paxId) => void;
  labels: {
    ccInputLabel: string;
    expiryInputLabel: string;
    cvdInputLabel: string;
    disclaimer: string;
    iframeTitle: string;
    errors: Record<string, string>;
  };
  path: string;
  variant?: CreditCardVariant;
  height?: string;
  width?: string;
  initiateTokenization?: boolean;
  onToken?: (e: { token: string; type: string }, paxId) => void;
  profileId?: string;
}

const CreditCardCoverMe: FC<{ cardError: string[]; children?: React.ReactNode }> = ({ children, cardError }) => {
  return (
    <>
      <div data-testid="cc-container-coverme" className="cc-container">
        {children}
      </div>
      {!isEmpty(cardError) && (
        <div className="payment-error">
          <Error errors={cardError} />
        </div>
      )}
    </>
  );
};

const CreditCardDefault: FC<{ cardError: string[]; disclaimer: string; children?: React.ReactNode }> = ({ children, cardError, disclaimer }) => {
  return (
    <div data-testid="cc-container-default" className="d-flex flex-column cc-fix-width">
      <div className="d-flex flex-column border alt-primary-highlight-brd white-bg px-3 py-5">
        <div className="d-flex flex-row">{children}</div>
        {!isEmpty(cardError) && <Error errors={cardError} className="mt-2" />}
      </div>
      {!isEmpty(disclaimer) && <div className="mt-4 mb-2">{disclaimer}</div>}
    </div>
  );
};

export const CreditCardForm: FC<CreditCardFormProps> = ({
  id,
  uri,
  labels,
  onDSToken,
  travellerPayments,
  onError,
  variant,
  path,
}) => {
  const [cardError, setCardError] = useState<string[]>();
  const isInternal = globalThis.location.hostname.includes('aks');
  const fieldIdx = parseInt(id.split('-')[1], 10);
  

  const onDSWrappedToken = useCallback(
    ({ token, type, hasError }, transactionId) => {
      const cardSupported = SUPPORTED_CC.includes(type);
      if (hasError || !cardSupported) {
        onError([labels.errors.mon_943], transactionId);
      } else {
        onError([], transactionId);
        onDSToken({ token, type }, transactionId);
      }
    },
    [onDSToken, labels, onError]
  );

  //temporary until DSIframe is available in dev and test
  const handleMockCreditCard = useCallback(e => {
    if (e.target.value.length >= MIN_LENGTH.CREDIT_CARD) {
      const token = e.target.value;
      const type = getCardType(e.target.value);
      const cardSupported = SUPPORTED_CC.includes(type);
      if (!cardSupported) {
        onError([labels.errors.mon_943], fieldIdx);
      } else {
        onError([], fieldIdx);
        onDSToken({ token, type }, fieldIdx);
      }
    }
  }, []);
  
  const handleParse = useCallback(value => trim(value), []);

  return isEqual(variant, 'COVER_ME') ? (
    isInternal ? (
      <>
        <CreditCardCoverMe cardError={cardError}>
          <label>{labels.ccInputLabel}</label>
          <Field //TODO: remove this field when DSIframe is available in dev and test
            name={id}
            component={GenericTextInput}
            type="text"
            id={id}
            aria-labelledby={id}
            parse={handleParse}
            validateOnActive={false}
            required={true}
            inputOnChange={handleMockCreditCard}
            mask={INPUT_MASK.PAYMENT_TOKEN}
          />
        </CreditCardCoverMe>
      </>
    ) : (
      <div className="cc-container" data-testid="cc-container-coverme">
        <label>{labels.ccInputLabel}</label>
        <CreditCardIFrame path={path} id={id} onMessage={onDSWrappedToken} iframeUrl={uri} />
        {travellerPayments && travellerPayments[fieldIdx] && (
          <Error errors={travellerPayments[fieldIdx]?.tokenError} />
        )}
      </div>
    )
  ) : isInternal ? (
    <>
      <CreditCardDefault cardError={cardError} disclaimer={`${labels.disclaimer}`}>
        <label>{labels.ccInputLabel}</label>
        <Field //TODO: remove this field when DSIframe is available in dev and test
          name={id}
          component={GenericTextInput}
          type="text"
          id={id}
          aria-labelledby={id}
          parse={handleParse}
          validateOnActive={false}
          required={true}
          inputOnChange={handleMockCreditCard}
          mask={INPUT_MASK.PAYMENT_TOKEN}
        />
      </CreditCardDefault>
    </>
  ) : (
    <div className="d-flex flex-column cc-fix-width" data-testid="cc-container-default">
      <label>{labels.ccInputLabel}</label>
      <CreditCardIFrame path={path} id={id} onMessage={onDSWrappedToken} iframeUrl={uri} />
      {travellerPayments && travellerPayments[fieldIdx] && <Error errors={travellerPayments[fieldIdx]?.tokenError} />}
    </div>
  );
};
